import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { CertificateDetail } from '../../features/project-detail/project-detail.component';
import { CertificateFormData } from '../../features/project-detail/certificate-form/certificate-form.component';
import config from '../../../config';

/**
 * Backend response structure for certificates
 */
interface CertificateBackendResponse {
  PK: string;
  SK: string;
  name: string;
  type: 'Front' | 'Back';
  project: string;
  environments: {
    dev?: {
      date: string;
      updatedAt: string;
      updatedBy?: string;
    };
    preprod?: {
      date: string;
      updatedAt: string;
      updatedBy?: string;
    };
    prod?: {
      date: string;
      updatedAt: string;
      updatedBy?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

/**
 * Service responsible for certificate business logic
 * Following SRP: handles only certificate-related operations
 */
@Injectable({
  providedIn: 'root',
})
export class CertificateService {
  apiUrl = config.API_GATEWAY_ENDPOINT;

  constructor(private readonly http: HttpClient) {}

  /**
   * Fetches all certificates for a specific project
   * @param projectName - The name of the project
   * @returns Promise with array of certificates
   */
  async fetchCertificates(projectName: string): Promise<CertificateDetail[]> {
    const response = await lastValueFrom(
      this.http.get<CertificateBackendResponse[]>(
        `${this.apiUrl}/certificates/${projectName}`,
      ),
    );

    // Transform backend response to CertificateFormData format
    const transformedData: CertificateFormData[] = response.map((cert) => ({
      name: cert.name,
      type: cert.type,
      dev: cert.environments.dev?.date,
      preprod: cert.environments.preprod?.date,
      prod: cert.environments.prod?.date,
    }));

    // Transform to CertificateDetail (calculate derived fields)
    const certificates = transformedData.map((cert) =>
      this.createCertificateFromFormData(cert),
    );

    return certificates;
  }

  /**
   * Updates a certificate environment date
   * @param projectName - The name of the project
   * @param certificateType - The type of certificate (Front or Back)
   * @param certificateName - The name of the certificate
   * @param environment - The environment to update (dev, preprod, prod)
   * @param date - The new expiration date (ISO format: YYYY-MM-DD)
   * @returns Promise with the updated certificate
   */
  async updateCertificate(
    projectName: string,
    certificateType: 'Front' | 'Back',
    certificateName: string,
    environment: 'dev' | 'preprod' | 'prod',
    date: string,
  ): Promise<CertificateDetail> {
    const updateData = {
      environment,
      date,
    };

    console.log('Updating certificate:', {
      projectName,
      certificateType,
      certificateName,
      updateData,
    });

    const response = await lastValueFrom(
      this.http.patch<CertificateBackendResponse>(
        `${this.apiUrl}/certificates/${projectName}/${certificateType}/${certificateName}`,
        updateData,
      ),
    );

    // Transform backend response to frontend format
    const transformedData: CertificateFormData = {
      name: response.name,
      type: response.type,
      dev: response.environments.dev?.date,
      preprod: response.environments.preprod?.date,
      prod: response.environments.prod?.date,
    };

    return this.createCertificateFromFormData(transformedData);
  }

  /**
   * Saves a certificate to the database
   * @param projectName - The name of the project
   * @param formData - The certificate form data
   * @returns Promise with the created certificate
   */
  async createCertificate(
    projectName: string,
    formData: CertificateFormData,
  ): Promise<CertificateDetail> {
    const certificateData = {
      project: projectName,
      ...formData,
    };

    console.log('Creating certificate:', certificateData);

    // Backend returns CertificateBackendResponse structure
    const response = await lastValueFrom(
      this.http.post<CertificateBackendResponse>(
        `${this.apiUrl}/certificates`,
        certificateData,
      ),
    );

    // Transform backend response to frontend format
    const transformedData: CertificateFormData = {
      name: response.name,
      type: response.type,
      dev: response.environments.dev?.date,
      preprod: response.environments.preprod?.date,
      prod: response.environments.prod?.date,
    };

    // Calculate derived fields and return CertificateDetail
    return this.createCertificateFromFormData(transformedData);
  }

  /**
   * Creates a certificate detail object from form data
   * @param formData - The form data from the certificate form
   * @returns A complete CertificateDetail object with calculated status
   */
  createCertificateFromFormData(
    formData: CertificateFormData,
  ): CertificateDetail {
    const hasExpired = this.checkIfCertificateExpired(
      formData.dev,
      formData.preprod,
      formData.prod,
    );

    return {
      name: formData.name,
      environment: this.buildEnvironmentLabel(
        formData.dev,
        formData.preprod,
        formData.prod,
      ),
      type: formData.type,
      expirationDate: formData.prod || formData.preprod || formData.dev || '',
      dev: formData.dev,
      preprod: formData.preprod,
      prod: formData.prod,
      status: hasExpired ? 'INVALID' : 'VALID',
      statusColor: hasExpired ? 'red' : 'green',
      icon: hasExpired ? '🚫' : '🔐',
    };
  }

  /**
   * Checks if any of the certificate's expiration dates have passed
   * @param dev - Dev environment expiration date
   * @param preprod - Preprod environment expiration date
   * @param prod - Prod environment expiration date
   * @returns true if any date has expired, false otherwise
   */
  checkIfCertificateExpired(
    dev?: string,
    preprod?: string,
    prod?: string,
  ): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare dates only

    const dates = [dev, preprod, prod].filter((d) => d);

    for (const dateStr of dates) {
      const expirationDate = new Date(dateStr!);
      expirationDate.setHours(0, 0, 0, 0);

      if (expirationDate < today) {
        return true;
      }
    }

    return false;
  }

  /**
   * Builds a human-readable label for environments
   * @param dev - Dev environment date
   * @param preprod - Preprod environment date
   * @param prod - Prod environment date
   * @returns A label like "Dev/Preprod" or "Prod"
   */
  buildEnvironmentLabel(dev?: string, preprod?: string, prod?: string): string {
    const envs: string[] = [];
    if (dev) envs.push('Dev');
    if (preprod) envs.push('Preprod');
    if (prod) envs.push('Prod');
    return envs.join('/');
  }

  /**
   * Validates certificate expiration dates
   * @param dev - Dev environment date
   * @param preprod - Preprod environment date
   * @param prod - Prod environment date
   * @returns true if at least one date is provided
   */
  hasAtLeastOneDate(dev?: string, preprod?: string, prod?: string): boolean {
    return !!(dev || preprod || prod);
  }
}
